import axios from 'axios';
import api from './api';
import { CfeDocument, CfePendingItem, CfeGenerateResult } from '../types/cfe';

const dgiService = {
  getReceiptWorkflow: (reciboId: number): Promise<{ data: {
    recibo_id: number; numero_recibo: string; required_types: number[]; documents: CfeDocument[];
  } }> => api.get(`/dgi/receipt-workflow/${reciboId}`),
  previewRegularization: (cfeDocumentId: number): Promise<{ data: {
    can_start: boolean; original_sha256?: string; serie: string; numero: number;
    fecha_emision?: string; importe?: string; moneda?: string; receptor?: string;
    replacement_cfe_id?: number | null; replacement_serie?: string | null; replacement_numero?: number | null;
    phase?: string; error?: string | null; finished?: boolean; execution_enabled?: boolean;
  } }> => api.post('/dgi/regularize-resguardo', { cfe_document_id: cfeDocumentId, action: 'preview' }),

  regularizeResguardo: (cfeDocumentId: number, originalSha?: string, resume = false): Promise<{
    success: boolean; message: string; phase?: string;
  }> => api.post('/dgi/regularize-resguardo', { cfe_document_id: cfeDocumentId,
    action: resume ? 'continue' : 'regularize', original_sha256: originalSha }),
  getResguardos: (estado: string, page: number): Promise<{ data: { rows: CfePendingItem[]; total: number; page: number; page_size: number } }> =>
    api.get('/dgi/resguardos', { params: { estado, page } }),

  syncStatus: (cfeDocumentId: number): Promise<{ success: boolean; message?: string; estado?: string }> =>
    api.post('/dgi/sync-status', { cfe_document_id: cfeDocumentId }),

  syncResguardos: (afterId: number, upToId?: number): Promise<{ data: {
    done: boolean; remaining: number; after_id: number; up_to_id: number;
    result?: { success: boolean; estado?: string; message?: string };
  } }> => api.post('/dgi/sync-status', { after_id: afterId, ...(upToId === undefined ? {} : { up_to_id: upToId }) }),
  /**
   * Get pending CFE documents (signed but not yet sent)
   */
  getPending: (): Promise<{ data: CfePendingItem[] }> => api.get('/dgi/pending'),

  /**
   * Get CFE status for a recibo (all associated CFE documents)
   */
  getStatus: (reciboId: number): Promise<{ data: CfeDocument[] }> => api.get(`/dgi/status/${reciboId}`),

  /**
   * Get a single CFE document detail
   */
  getDocument: (cfeDocId: number): Promise<{ data: CfeDocument }> => api.get(`/dgi/document/${cfeDocId}`),

  /**
   * Generate CFE documents for a recibo (manual trigger)
   */
  generar: (reciboId: number): Promise<CfeGenerateResult> => api.post('/dgi/generar', { recibo_id: reciboId }),

  /**
   * Send a single CFE to DGI (manual confirmation)
   */
  enviar: (cfeDocumentId: number): Promise<{ success: boolean; message: string }> =>
    api.post('/dgi/enviar', { cfe_document_id: cfeDocumentId }),

  /**
   * Send multiple CFEs to DGI in batch
   */
  enviarBatch: (cfeDocumentIds: number[]): Promise<{ data: Array<{ cfe_document_id: number; result: { success: boolean; message: string } }> }> =>
    api.post('/dgi/enviar-batch', { cfe_document_ids: cfeDocumentIds }),

  /**
   * Download official DGI PDF for a CFE document
   */
  downloadPdf: async (cfeDocId: number): Promise<Blob | null> => {
    const baseURL = (api.defaults as any).baseURL || 'http://localhost/backend';
    const response = await axios.get(`${baseURL}/dgi/pdf/${cfeDocId}`, {
      responseType: 'blob',
      withCredentials: true,
    });
    return response.data;
  },
};

export default dgiService;
